import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import { Container, Row, Col, Button, Spinner, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { TUTOR_ANALYTIC_API } from "../../../api/api";

import Chart from "react-apexcharts";
import { FaStar, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

// import excelImg from "../../../assets/excel-icon.png";
// import totalViewsImg from "../../../assets/tutor-student-dou-icon1.png";
// import avgCompletionImg from "../../../assets/tutor-student-dou-icon2.png";
// import totalstudentImg from "../../../assets/tutor-student-dou-icon4.png";
// import AvgRatingImg from "../../../assets/tutor-student-dou-icon5.png";

// latestIcons
import totalViews from "../../../assets/icons/tutor/total-views.svg";
// import totalstudent from "../../../assets/icons/tutor/active-students.svg";
import totalstudent from "../../../assets/icons/admin/enrolled-students.svg";
import avgCompletion from "../../../assets/icons/tutor/avg-completion.svg";
import AvgRating from "../../../assets/icons/tutor/average-rating.svg";
// import totalEarning from "../../../assets/icons/tutor/total-earnings.svg";
// import pendingPayouts from "../../../assets/icons/tutor/pending-payouts.svg";
import Nofound from "../../../assets/not-found.png";

import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./TutorAnalytics.css";

const TutorAnalytics = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("Course Analytics");
  const [analyticsStats, setAnalyticsStats] = useState({});
  const [analyticsData, setAnalyticsData] = useState({});

  // Filter states
  const [selectedCourseFilter, setSelectedCourseFilter] =
    useState("All Courses");
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState("All Students");

  //loading
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setLoadingAnalytics(false);
      navigate("/signin");

      return;
    }

    const fetchAnalytics = async () => {
      try {
        const response = await axios.post(
          TUTOR_ANALYTIC_API.GET_ANALYTICS_ADMIN_DASHBOARD,
          { tutor_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setAnalyticsStats(data.dashboard.stats || {});
          setAnalyticsData(data.dashboard || {});
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error analytics:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setLoadingAnalytics(false);
      }
    };

    fetchAnalytics();
  }, [navigate]);

  const topCourses = useMemo(() => {
    return analyticsData.topCourses || [];
  }, [analyticsData.topCourses]);

  const topStudents = useMemo(() => {
    return analyticsData.studentPerformance || [];
  }, [analyticsData.studentPerformance]);

  const monthlyGraph = useMemo(() => {
    return analyticsData.monthlyGraph || [];
  }, [analyticsData.monthlyGraph]);

  const gradeChart = useMemo(() => {
    return analyticsData.gradeChart || {};
  }, [analyticsData.gradeChart]);

  const engagement = useMemo(() => {
    return analyticsData.engagementMetrics || {};
  }, [analyticsData.engagementMetrics]);

  const filteredCoursesForDropdown = useMemo(() => {
    return [...new Set(topStudents.map((s) => s.course_title))];
  }, [topStudents]);

  const filteredStudentsForDropdown = useMemo(() => {
    return [...new Set(topStudents.map((s) => s.student_name))];
  }, [topStudents]);

  const filteredStudents = useMemo(() => {
    return topStudents.filter((s) => {
      const matchCourse =
        selectedCourseFilter === "All Courses" ||
        s.course_title === selectedCourseFilter;

      const matchStudent =
        selectedStudentFilter === "All Students" ||
        s.student_name === selectedStudentFilter;

      return matchCourse && matchStudent;
    });
  }, [topStudents, selectedCourseFilter, selectedStudentFilter]);

  const clearFiltersStudent = () => {
    setSelectedCourseFilter("All Courses");
    setSelectedStudentFilter("All Students");
  };

  const { sortedData, sortConfig, handleSort } =
    useSortableTable(filteredStudents);

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  const courseCategories = monthlyGraph?.map((item) => item.month) || [];

  // charts
  const courseTrendOptions = {
    chart: {
      id: "course-trend",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: "smooth", width: 3 },
    xaxis: {
      categories: courseCategories,
      labels: { style: { colors: "#6c757d", fontSize: "12px" } },
      axisBorder: { color: "#e0e0e0" },
      axisTicks: { color: "#e0e0e0" },
    },
    yaxis: { labels: { style: { colors: "#6c757d", fontSize: "12px" } } },
    grid: {
      borderColor: "#e9ecef",
      strokeDashArray: 4,
      padding: { top: 10, right: 20, bottom: 10, left: 15 },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "12px",
      markers: { width: 10, height: 10, radius: 2 },
    },
    // colors: ["#28a745", "#f0ad4e", "#0d6efd"],
  };

  const allCourses = useMemo(() => {
    return [
      ...new Set(
        monthlyGraph.flatMap((m) =>
          (m.courses || []).map((c) => c.course_title.trim()),
        ),
      ),
    ];
  }, [monthlyGraph]);

  const courseWiseSeries = useMemo(() => {
    return allCourses.map((course) => ({
      name: course,
      data: monthlyGraph.map((month) => {
        const found = (month.courses || []).find(
          (c) => c.course_title.trim() === course,
        );
        return found ? Number(found.enrollments) : 0;
      }),
    }));
  }, [allCourses, monthlyGraph]);

  const gradeDistOptions = {
    chart: {
      id: "grade-dist",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        columnWidth: "45%",
        endingShape: "flat",
      },
    },
    dataLabels: {
      enabled: false, //  <<< THIS REMOVES NUMBERS ON TOP OF BARS
    },
    colors: ["#136AD0"],
    xaxis: {
      categories: gradeChart.xAxis || [],
      labels: {
        style: {
          fontSize: "12px",
          colors: "#333",
        },
      },
    },
    grid: {
      borderColor: "#eaeaea",
      strokeDashArray: 4,
    },
    tooltip: {
      theme: "light",
    },
  };

  const gradeDistSeries = [
    {
      name: "Students",
      data: (gradeChart.yAxis || []).map(Number),
    },
  ];

  if (loadingAnalytics) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // const revenueTrendOptions = {
  //   chart: {
  //     id: "revenue-trend",
  //     toolbar: { show: false },
  //     zoom: { enabled: false },
  //   },
  //   stroke: { width: 2.5, curve: "smooth" },
  //   colors: ["#22c55e", "#2563eb"],
  //   xaxis: {
  //     categories: ["Jun", "Jul", "Aug", "Sep", "Oct"],
  //     labels: { style: { fontSize: "12px", colors: "#6b7280" } },
  //   },
  //   grid: { borderColor: "#e9ecef", strokeDashArray: 4 },
  //   markers: { size: 3, strokeWidth: 2, strokeColors: "#fff" },
  //   legend: {
  //     position: "bottom",
  //     fontSize: "12px",
  //     markers: { width: 8, height: 8, radius: 12 },
  //   },
  // };

  // const revenueTrendSeries = [
  //   { name: "Revenue (₹)", data: [18000, 20000, 23000, 26000, 29000] },
  //   { name: "New Students", data: [10, 15, 12, 20, 11] },
  // ];

  // const revenueByCourseOptions = {
  //   chart: {
  //     id: "revenue-pie",
  //     toolbar: { show: false },
  //   },
  //   labels: ["HTML", "JavaScript", "React"],
  //   tooltip: {
  //     enabled: false,
  //   },
  // };

  // const revenueByCourseSeries = [41, 33, 26];

  return (
    <Container className="tutor-analytics-section-container">
      <h4 className="tutor-feedback-title">Teaching Analytics</h4>
      <p className="tutor-feedback-subtitle">
        Track your course performance and student engagement. 
      </p>

      {/* Tabs */}
      <div className="tutor-analytics-section-tabs">
        {[
          "Course Analytics",
          "Student Performance",
          // "Revenue Analytics"
        ].map((tab) => (
          <Button
            key={tab}
            className={`tutor-analytics-section-tab ${
              activeTab === tab ? "active" : ""
            }`}
            onClick={() => setActiveTab(tab)}
            size="sm"
          >
            {tab}
          </Button>
        ))}
      </div>

      <Row className="tutor-analytics-section-content-row">
        {activeTab === "Course Analytics" && (
          <>
            <Col className="tutor-analytics-section-course-col">
              <Row className="tutor-analytics-section-cards-row">
                <Col lg={3} md={4} sm={6} xs={12}>
                  <div className="tutor-analytics-section-stat-card">
                    <h6 className="tutor-analytics-section-stat-title">
                      Total Course
                    </h6>
                    <img
                      src={totalViews}
                      className="tutor-analytics-stat-icon"
                      alt="views"
                    />

                    <h3 className="tutor-analytics-section-stat-value">
                      {Number(analyticsStats.total_course) || "-"}
                    </h3>
                    <span className="tutor-analytics-section-stat-sub">
                      +12% from last month
                    </span>
                  </div>
                </Col>

                <Col lg={3} md={4} sm={6} xs={12}>
                  <div className="tutor-analytics-section-stat-card">
                    <h6 className="tutor-analytics-section-stat-title">
                      Enrolled Students
                    </h6>
                    <img
                      src={totalstudent}
                      className="tutor-analytics-stat-icon"
                      alt="students"
                    />

                    <h3 className="tutor-analytics-section-stat-value">
                      {Number(analyticsStats.total_students) || "-"}
                    </h3>
                    <span className="tutor-analytics-section-stat-sub">
                      +8% w/o churn
                    </span>
                  </div>
                </Col>

                <Col lg={3} md={4} sm={6} xs={12}>
                  <div className="tutor-analytics-section-stat-card">
                    <h6 className="tutor-analytics-section-stat-title">
                      Avg Final Assignment Completion
                    </h6>
                    <img
                      src={avgCompletion}
                      className="tutor-analytics-stat-icon"
                      alt="completion"
                    />

                    <h3 className="tutor-analytics-section-stat-value">
                      {Number(analyticsStats.avg_completion) > 0
                        ? `${analyticsStats.avg_completion}%`
                        : "-"}
                    </h3>
                    <span className="tutor-analytics-section-stat-sub">
                      +3% vs last month
                    </span>
                  </div>
                </Col>

                <Col lg={3} md={4} sm={6} xs={12}>
                  <div className="tutor-analytics-section-stat-card">
                    <h6 className="tutor-analytics-section-stat-title">
                      Avg Rating
                    </h6>
                    <img
                      src={AvgRating}
                      className="tutor-analytics-stat-icon"
                      alt="rating"
                    />

                    <h3 className="tutor-analytics-section-stat-value">
                      {Number(analyticsStats.avg_rating) || "-"}
                    </h3>
                    <span className="tutor-analytics-section-stat-sub">
                      Based on 3,400 reviews
                    </span>
                  </div>
                </Col>
              </Row>

              <Row className="tutor-analytics-section-charts-row g-3 mt-2">
                <Col md={6}>
                  <div className="tutor-analytics-section-chart-card">
                    <div className="tutor-analytics-section-chart-title">
                      Course Purchased Trends
                    </div>
                    {courseWiseSeries.length > 0 ? (
                      <Chart
                        options={courseTrendOptions}
                        series={courseWiseSeries}
                        type="line"
                        height={260}
                      />
                    ) : (
                      <p className="tutor-dashboard-no-data mt-5">
                        No chart data available
                      </p>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="tutor-analytics-section-chart-card">
                    <div className="tutor-analytics-section-chart-title">
                      Student Grade Distribution
                    </div>
                    {gradeDistSeries[0].data.some((v) => v > 0) ? (
                      <Chart
                        options={gradeDistOptions}
                        series={gradeDistSeries}
                        type="bar"
                        height={260}
                      />
                    ) : (
                      <p className="tutor-dashboard-no-data mt-5">
                        No grade data available
                      </p>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Top courses table */}
              <Row className="tutor-analytics-section-topcourses-row mt-5">
                <Col>
                  <div className="tutor-analytics-section-topcourses-card">
                    <div className="tutor-analytics-section-topcourses-header mb-2">
                      <h5 className="tutor-analytics-section-topcourses-title">
                        Courses Performance
                      </h5>
                      <div className="tutor-analytics-section-topcourses-controls d-flex gap-1">
                        {/* <Form.Select
                          size="sm"
                          className="tutor-analytics-section-topcourses-filter"
                          value={courseFilter}
                          onChange={(e) => setCourseFilter(e.target.value)}
                        >
                          <option value="3m">Last 3 Months</option>
                          <option value="1m">Last 1 Month</option>
                          <option value="7d">Last 7 Days</option>
                          <option value="1d">Last 1 Day</option>
                          <option value="all">All Time</option>
                        </Form.Select> */}

                        {/* <Button
                          size="sm"
                          className="tutor-analytics-section-topcourses-export"
                          onClick={handleExportTopCourses}
                        >
                          <img
                            src={excelImg}
                            alt="Excel"
                            className="tutor-analytics-section-excel-img"
                          />
                        </Button> */}
                      </div>
                    </div>

                    <div className="tutor-analytics-section-topcourses-table-wrapper mt-3">
                      <table className="tutor-analytics-section-topcourses-table w-100">
                        <thead className="tutor-analytics-section-topcourses-thead">
                          <tr className="tutor-analytics-section-topcourses-tr">
                            <th className="tutor-analytics-section-th">
                              Course
                            </th>
                            {/* <th className="tutor-analytics-section-th">
                              Total Views
                            </th> */}
                            <th className="tutor-analytics-section-th">
                              Completion Rate
                            </th>
                            <th className="tutor-analytics-section-th">
                              Avg Rating
                            </th>
                            <th className="tutor-analytics-section-th">
                              Students
                            </th>
                          </tr>
                        </thead>
                        <tbody className="tutor-analytics-section-topcourses-tbody tutor-analytics-scroll">
                          {topCourses.length > 0 ? (
                            topCourses.map((c, idx) => (
                              <tr
                                key={idx}
                                className="tutor-analytics-section-topcourses-tr"
                              >
                                <td className="tutor-analytics-section-td">
                                  {c.course_title || "-"}
                                </td>
                                {/* <td className="tutor-analytics-section-td">
                                  {c.total_views || "-"}
                                </td> */}
                                <td className="tutor-analytics-section-td">
                                  <div className="d-flex justify-content-between align-items-center w-100">
                                    <div className="tutor-analytics-section-progress-bar-outer flex-grow-1 me-2">
                                      <div
                                        className="tutor-analytics-section-progress-bar-inner"
                                        style={{
                                          width: `${Number(c.completion_rate) || 0}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="tutor-analytics-section-progress-percent">
                                      {Number(c.completion_rate) > 0
                                        ? `${c.completion_rate}%`
                                        : "-"}
                                    </span>
                                  </div>
                                </td>

                                <td className="tutor-analytics-section-td d-flex align-items-center gap-1">
                                  <FaStar size={14} color="#FFD700" />
                                  {c.avg_rating || "-"}
                                </td>
                                <td className="tutor-analytics-section-td">
                                  {c.students || "-"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="5"
                                className="tutor-analytics-section-td text-center"
                              >
                                <img
                                  src={Nofound}
                                  alt="No Data Found"
                                  className="admin-all-tutor-empty-img"
                                />
                                <p className="tutor-dashboard-no-data">
                                  No Data Found
                                </p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </>
        )}

        {/*STUDENT TAB  */}
        {activeTab === "Student Performance" && (
          <>
            <Col className="tutor-analytics-section-student-col">
              <div className="tutor-analytics-section-student-card">
                <div className="d-flex justify-content-between align-items-center tutor-analytics-section-student-header">
                  <h5 className="tutor-analytics-section-topcourses-title">
                    Final Assignment Top Students
                  </h5>

                  <div className="d-flex gap-3">
                    <Form.Select
                      // size="sm"
                      value={selectedCourseFilter}
                      onChange={(e) => setSelectedCourseFilter(e.target.value)}
                      className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
                    >
                      <option value="All Courses">All Courses</option>
                      {filteredCoursesForDropdown.map((course, index) => (
                        <option key={index} value={course}>
                          {course}
                        </option>
                      ))}
                    </Form.Select>

                    <Form.Select
                      // size="sm"
                      value={selectedStudentFilter}
                      onChange={(e) => setSelectedStudentFilter(e.target.value)}
                      className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
                    >
                      <option value="All Students">All Students</option>
                      {filteredStudentsForDropdown.map((student, index) => (
                        <option key={index} value={student}>
                          {student}
                        </option>
                      ))}
                    </Form.Select>

                    <Button
                      // size="sm"
                      className="tutor-analytics-section-student-export"
                      onClick={clearFiltersStudent}
                    >
                      Clear
                    </Button>
                    {/* <Button
                      variant="outline-success"
                      size="sm"
                      onClick={handleExportStudents}
                      className="tutor-analytics-section-student-export"
                    >
                      <img
                        src={excelImg}
                        alt="export"
                        className="tutor-analytics-export-img"
                      />
                    </Button> */}
                  </div>
                </div>

                <div className="tutor-analytics-section-student-table-wrapper mt-3">
                  <table className="tutor-analytics-section-student-table w-100">
                    <thead>
                      <tr>
                        <th
                          className="tutor-analytics-section-th"
                          onClick={() => handleSort("student_name")}
                          style={{ cursor: "pointer" }}
                        >
                          Student Name {renderSortIcon("student_name")}
                        </th>
                        <th
                          className="tutor-analytics-section-th"
                          onClick={() => handleSort("course_title")}
                          style={{ cursor: "pointer" }}
                        >
                          Course {renderSortIcon("course_title")}
                        </th>
                        <th
                          className="tutor-analytics-section-th"
                          onClick={() => handleSort("grade")}
                          style={{ cursor: "pointer" }}
                        >
                          Grade {renderSortIcon("grade")}
                        </th>
                        <th
                          className="tutor-analytics-section-th"
                          onClick={() => handleSort("total_marks")}
                          style={{ cursor: "pointer" }}
                        >
                          Score {renderSortIcon("total_marks")}
                        </th>
                        {/* <th className="tutor-analytics-section-th">
                          Activity Status
                        </th> */}
                      </tr>
                    </thead>

                    <tbody className="tutor-analytics-section-student-tbody">
                      {sortedData.length > 0 ? (
                        sortedData.map((s, idx) => (
                          <tr key={idx}>
                            <td className="tutor-analytics-section-td">
                              {s.student_name || "-"}
                            </td>
                            <td className="tutor-analytics-section-td">
                              {s.course_title || "-"}
                            </td>
                            <td className="tutor-analytics-section-td">
                              {/* <div className="tutor-analytics-section-progress">
                                <div
                                  className="tutor-analytics-section-progress-inner"
                                  style={{
                                    width: `${Number(s.progress) || 0}%`,
                                  }}
                                />
                              </div> */}
                              {s.grade || "-"}
                            </td>
                            <td className="tutor-analytics-section-td">
                              {s.total_marks}
                            </td>
                            {/* <td className="tutor-analytics-section-td">
                            <span
                              className={`tutor-analytics-section-badge ${
                                s.status === "Active" ? "active" : "inactive"
                              }`}
                            >
                              {s.status}
                            </span>
                          </td> */}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            className="tutor-analytics-section-td text-center"
                          >
                            <img
                              src={Nofound}
                              alt="No Data Found"
                              className="admin-all-tutor-empty-img"
                            />
                            <p className="tutor-dashboard-no-data">
                              No Data Found
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <Row className="tutor-analytics-section-engagement-row mt-5 p-0">
                  <h5 className="tutor-analytics-section-topcourses-title mb-4">
                    Student Engagement Metrics
                  </h5>
                  <Col md={4}>
                    <div className="tutor-analytics-section-engagement-card p-3">
                      <div className="tutor-analytics-section-engagement-title">
                        Enrolled Students
                      </div>
                      <div className="tutor-analytics-section-engagement-value">
                        {Number(engagement.active_students) || "-"}
                      </div>
                      <div className="tutor-analytics-section-engagement-sub">
                        95% retention rate
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="tutor-analytics-section-engagement-cards p-3">
                      <div className="tutor-analytics-section-engagement-title">
                        Avg Study Time
                      </div>
                      <div className="tutor-analytics-section-engagement-value">
                      
                        {(engagement.avg_study_time) || 0} hrs
                      </div>
                      <div className="tutor-analytics-section-engagement-sub">
                        Above platform avg
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="tutor-analytics-section-engagement-cardss p-3">
                      <div className="tutor-analytics-section-engagement-title">
                        Avg Final Assignment Completion
                      </div>
                      <div className="tutor-analytics-section-engagement-value">
                        {Number(engagement.assignment_completion) > 0
                          ? `${engagement.assignment_completion}%`
                          : "-"}
                        {/* {Number(engagement.assignment_completion) || "-"}% */}
                      </div>
                      <div className="tutor-analytics-section-engagement-sub">
                        +7% from last month
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </>
        )}

        {/* REVENUE TAB  */}
        {/* {activeTab === "Revenue Analytics" && (
          <>
            <Col className="tutor-analytics-section-revenue-col">
              <div className="tutor-analytics-section-revenue-card">
                <Row className="tutor-analytics-section-cards-row">
                  <Col lg={4} md={4} sm={6} xs={12}>
                    <div className="tutor-analytics-section-stat-card">
                      <h6 className="tutor-analytics-section-stat-title">
                        Total Earnings
                      </h6>
                      <img
                        src={totalEarning}
                        className="tutor-analytics-stat-icon"
                        alt="earnings"
                      />
                      <h3 className="tutor-analytics-section-stat-value">
                        ₹1,33,000
                      </h3>
                      <span className="tutor-analytics-section-stat-sub">
                        +23 this week
                      </span>
                    </div>
                  </Col>

                  <Col lg={4} md={4} sm={6} xs={12}>
                    <div className="tutor-analytics-section-stat-card">
                      <h6 className="tutor-analytics-section-stat-title">
                        Pending Payouts
                      </h6>
                      <img
                        src={pendingPayouts}
                        className="tutor-analytics-stat-icon"
                        alt="payouts"
                      />
                      <h3 className="tutor-analytics-section-stat-value">
                        ₹25,000
                      </h3>
                      <span className="tutor-analytics-section-stat-sub">
                        +23 this week
                      </span>
                    </div>
                  </Col>

                  <Col lg={4} md={4} sm={6} xs={12}>
                    <div className="tutor-analytics-section-stat-card">
                      <h3 className="tutor-analytics-section-stat-title">
                        Avg per Student
                      </h3>
                      <img
                        src={totalstudent}
                        className="tutor-analytics-stat-icon"
                        alt="students"
                      />
                      <h6 className="tutor-analytics-section-stat-value">
                        ₹364
                      </h6>
                      <span className="tutor-analytics-section-stat-sub">
                        +8% increase
                      </span>
                    </div>
                  </Col>
                </Row>

                <div className="tutor-analytics-revenue-card p-3 mt-2">
                  <div className="tutor-analytics-revenue-title">
                    Monthly Revenue Trend
                  </div>
                  <Chart
                    options={revenueTrendOptions}
                    series={revenueTrendSeries}
                    type="line"
                    height={260}
                  />
                </div>

                <Row className="mt-4 tutor-analytics-main-row">
                  <Col md={6}>
                    <div className="tutor-analytics-pie-card p-3">
                      <div className="tutor-analytics-revenue-title">
                        Revenue by Course
                      </div>
                      <Chart
                        options={revenueByCourseOptions}
                        series={revenueByCourseSeries}
                        type="pie"
                        height={260}
                      />
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="tutor-analytics-payment-card p-3">
                      <div className="tutor-analytics-payment-title">
                        Payment Summary
                      </div>
                      <div className="tutor-analytics-payment-box tutor-analytics-green-bg">
                        <div className="tutor-analytics-payment-left">
                          Royalty-Based Earnings
                          <h4 className="tutor-analytics-payment-amount">
                            ₹79,800
                          </h4>
                        </div>
                        <div className="tutor-analytics-payment-badge tutor-analytics-payment-green-badge">
                          65%
                        </div>
                      </div>
                      <div className="tutor-analytics-payment-box tutor-analytics-blue-bg">
                        <div className="tutor-analytics-payment-left">
                          One-Time Settlement
                          <h4 className="tutor-analytics-payment-amount">
                            ₹53,200
                          </h4>
                        </div>
                        <div className="tutor-analytics-payment-badge tutor-analytics-payment-blue-badge">
                          40%
                        </div>
                      </div>
                      <div className="tutor-analytics-payment-box tutor-analytics-yellow-bg">
                        <div className="tutor-analytics-payment-left">
                          Platform Fee
                          <h4 className="tutor-analytics-payment-amount">
                            - ₹13,300
                          </h4>
                        </div>
                        <div className="tutor-analytics-payment-badge tutor-analytics-payment-yellow-badge">
                          15%
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </>
        )} */}
      </Row>
    </Container>
  );
};

export default TutorAnalytics;
